import type { Client } from '@urql/core'
import { runInAction } from 'mobx'
import { graphql } from 'app/generated/gql'

import type { Pipeline as PipelineType } from 'app/generated/graphql'
import {
  isAssignablePipeline,
  mergeSystemPipelinesAsPipelines,
  systemPipeline,
  toPipelineType,
} from 'app/components/action-center/systemPipelines'
import { isCompanionPipeline } from 'lib/companionPipelines'
import { EntityStore, ListParams } from './EntityStore'

// pipelines returns [Pipeline!]! directly — no args, no pagination wrapper
type PipelineListResult = { hits: PipelineType[]; total: number; page: number }

const PIPELINE_FIELDS = `id name orgId workspaceId stages { name color order }`

const LIST_QUERY = graphql(`
  query PipelinesStore {
    pipelines {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

const GET_QUERY = graphql(`
  query Pipeline($id: ID!) {
    pipeline(id: $id) {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreatePipeline($input: CreatePipelineInput!) {
    createPipeline(input: $input) {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdatePipeline($id: ID!, $input: UpdatePipelineInput!) {
    updatePipeline(id: $id, input: $input) {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeletePipeline($id: ID!) {
    deletePipeline(id: $id)
  }
`)

type FetchOpts = { requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only' }

/**
 * A full urql `OperationResult`. The system-pipeline branch below never sends a
 * request, so it casts a stand-in carrying only `.data`/`.error` (all callers read).
 */
type FetchOneResult = Awaited<ReturnType<EntityStore<PipelineType, PipelineListResult>['fetchOne']>>

export class PipelineStore extends EntityStore<
  PipelineType,
  PipelineListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `Pipeline`,
      extractList: (data: any) => ({ hits: data.pipelines, total: data.pipelines.length, page: 0 }),
      extractOne: (data: any) => data.pipeline,
      extractMutated: (data: any) => data.updatePipeline ?? data.createPipeline,
    })
  }

  /**
   * `hits` minus the system pipelines: the set a generic picker may offer. A system
   * pipeline is written only by its own board; a membership added elsewhere may never
   * match that board's search, leaving it invisible and impossible to clear. `hits`
   * itself stays unfiltered so read paths can still resolve stages.
   */
  get assignableHits(): PipelineType[] {
    // Wrapped, not passed by reference: `isAssignablePipeline` takes an optional
    // contact as its second argument and `filter` would hand it the index.
    return this.hits
      .filter((pipeline) => isAssignablePipeline(pipeline))
      .filter((pipeline) => !isCompanionPipeline(pipeline))
  }

  /**
   * System pipelines are frontend constants (see `systemPipelines.ts`); the server
   * 404s on their ids, so they are answered here without a network round trip.
   */
  async fetchOne(id: string, opts?: FetchOpts) {
    const system = systemPipeline(id)
    if (!system) return super.fetchOne(id, opts)

    const pipeline = toPipelineType(system)
    runInAction(() => {
      this.currentLoading = false
      this.currentError = undefined
      this.current = pipeline
    })
    return { data: { pipeline }, error: undefined } as unknown as FetchOneResult
  }

  async fetchList(params: ListParams = {}, opts?: FetchOpts) {
    const result = await super.fetchList(params, opts)
    if (!result.data) return result

    const data = result.data as { pipelines: PipelineType[] }
    const merged = mergeSystemPipelinesAsPipelines(data.pipelines)
    runInAction(() => {
      this.list = { hits: merged, total: merged.length, page: 0 }
    })
    return { ...result, data: { ...data, pipelines: merged } }
  }
}
